import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import usersStyles from '../components/dashboard/UsersSection.module.css';
import { createUser } from '../../../api/adminApi';

const inputStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid var(--admin-border,var(--ds-border))',
  fontSize: 13,
  width: '100%',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--admin-text-muted,var(--ds-text-muted))',
  marginBottom: 3,
  display: 'block',
};

const fieldStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 3 };
const rowStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };

const sectionHeader: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--admin-text-muted,var(--ds-text-faint))',
  borderBottom: '1px solid var(--admin-border,var(--ds-border))',
  paddingBottom: 4,
  marginTop: 8,
};

// Generate a password satisfying backend (≥1 letter + ≥1 digit) and
// Cognito (≥1 upper + ≥1 lower + ≥1 digit + ≥1 special) rules.
// Previously picked uniformly at random, could return digit-free strings
// like "u(x_@@r$SpD)" which the backend rejected with HTTP 400.
const generatePassword = (len = 12) => {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const digit = '0123456789';
  const special = '!@#$%^&*()_+-=';
  const pick = (s: string) => s.charAt(Math.floor(Math.random() * s.length));
  const chars: string[] = [pick(upper), pick(lower), pick(digit), pick(special)];
  const all = upper + lower + digit + special;
  while (chars.length < len) chars.push(pick(all));
  for (let i = chars.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
};

export default function AddEnterpriseUserPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [planTier, setPlanTier] = useState('enterprise');
  const [billingCycle, setBillingCycle] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [seats, setSeats] = useState('');
  const [amountPaid, setAmountPaid] = useState('');
  const [amountPending, setAmountPending] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) { setError('Email is required'); return; }
    if (!periodEnd) { setError('Valid until date is required'); return; }
    const pwd = password || generatePassword();
    setSubmitting(true);
    setError(null);
    try {
      const token = window.localStorage.getItem('policyora_token') || '';
      await createUser(token, {
        email,
        full_name: name || undefined,
        password: pwd,
        plan_tier: planTier,
        organization_name: orgName || undefined,
        billing_cycle: billingCycle || undefined,
        period_start: periodStart || undefined,
        period_end: periodEnd,
        seats: seats ? parseInt(seats, 10) : undefined,
        amount_paid: amountPaid ? parseFloat(amountPaid) : undefined,
        amount_pending: amountPending ? parseFloat(amountPending) : undefined,
        admin_notes: adminNotes || undefined,
      });
      navigate('/admin/dashboard/users/manage?category=Enterprises');
    } catch (err: any) {
      setError(err?.message || 'Failed to provision account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: '1.5rem', maxWidth: 720, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button className={usersStyles.modalBackButton} onClick={() => navigate(-1)}>← Back</button>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Add Enterprise User</h2>
          <div style={{ color: 'var(--admin-text-muted,var(--ds-text-muted))', fontSize: 13, marginTop: 2 }}>
            Provision an enterprise account with subscription details
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ background: 'var(--admin-surface,#fff)', borderRadius: 10, padding: 24, border: '1px solid var(--admin-border,var(--ds-border))', display: 'flex', flexDirection: 'column', gap: 16 }}>

          <div style={sectionHeader}>Account Details</div>

          <div style={rowStyle}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Email *</label>
              <input value={email} onChange={e => setEmail(e.target.value)} placeholder="user@company.com" style={inputStyle} required />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Full name</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Full name" style={inputStyle} />
            </div>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Company / Organization name</label>
            <input value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="Acme Corp" style={inputStyle} />
          </div>

          <div style={sectionHeader}>Subscription Details</div>

          <div style={rowStyle}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Plan</label>
              <select value={planTier} onChange={e => setPlanTier(e.target.value)} style={inputStyle}>
                <option value="enterprise">Enterprise</option>
                <option value="business">Business</option>
                <option value="growth">Growth</option>
              </select>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Billing cycle</label>
              <select value={billingCycle} onChange={e => setBillingCycle(e.target.value)} style={inputStyle}>
                <option value="">Not set</option>
                <option value="annual">Annual</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>

          <div style={rowStyle}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Valid from</label>
              <input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} style={inputStyle} />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Valid until *</label>
              <input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} style={inputStyle} required />
            </div>
          </div>

          <div style={rowStyle}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Number of seats</label>
              <input type="number" min={1} value={seats} onChange={e => setSeats(e.target.value)} placeholder="e.g. 25" style={inputStyle} />
            </div>
            <div style={fieldStyle} />
          </div>

          <div style={rowStyle}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Amount paid (USD)</label>
              <input type="number" min={0} step="0.01" value={amountPaid} onChange={e => setAmountPaid(e.target.value)} placeholder="e.g. 5000" style={inputStyle} />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Amount pending (USD)</label>
              <input type="number" min={0} step="0.01" value={amountPending} onChange={e => setAmountPending(e.target.value)} placeholder="e.g. 2499" style={inputStyle} />
            </div>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Admin notes (internal only)</label>
            <textarea
              value={adminNotes}
              onChange={e => setAdminNotes(e.target.value)}
              placeholder="e.g. Referred by sales team, custom contract terms..."
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>

          <div style={sectionHeader}>Access</div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <div style={{ flex: 1, ...fieldStyle }}>
              <label style={labelStyle}>Temporary password</label>
              <input
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Leave empty to auto-generate"
                type={showPassword ? 'text' : 'password'}
                style={inputStyle}
              />
            </div>
            <button type="button" className={usersStyles.userSegmentActionPrimary} style={{ whiteSpace: 'nowrap' }} onClick={() => setPassword(generatePassword())}>Generate</button>
            <button type="button" className={usersStyles.modalBackButton} style={{ whiteSpace: 'nowrap' }} onClick={() => setShowPassword(s => !s)}>{showPassword ? 'Hide' : 'Show'}</button>
          </div>

          {error && (
            <div style={{ color: 'var(--ds-danger-text)', fontSize: 13, background: 'var(--ds-danger-bg)', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--ds-danger-border)' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button type="button" className={usersStyles.modalBackButton} onClick={() => navigate(-1)}>Cancel</button>
            <button type="submit" className={usersStyles.userSegmentActionPrimary} disabled={submitting}>
              {submitting ? 'Provisioning…' : 'Provision enterprise account'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
